import React, { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, StatusChip } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { barcodeOverrideApi, storeActionSentence, type BarcodeOverrideRecord, type BarcodeStoreAction } from './barcodeOverrideApi';

/**
 * What became of the stores after an override of this staged row, and the way to finish it: the
 * request stops at its time budget, and an admin who chose to notify may push later.
 */
export const BarcodeOverrideHistory: React.FC<{ stagedId: string }> = ({ stagedId }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['barcode-overrides', stagedId];
  const { data } = useQuery({ queryKey, queryFn: () => barcodeOverrideApi.list(stagedId) });
  const [confirm, setConfirm] = useState<{ record: BarcodeOverrideRecord; action: BarcodeStoreAction } | null>(null);
  const run = useMutation({
    mutationFn: () => barcodeOverrideApi.storeAction(stagedId, confirm!.record.id, confirm!.action),
    onSuccess: (summary) => {
      showToast(storeActionSentence(summary), summary.failed > 0 ? 'warning' : 'success');
      setConfirm(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const records = (data || []).filter((record) => record.corrections.length > 0);
  if (records.length === 0) return null;
  return (
    <>
      {records.map((record) => {
        const open = record.corrections.filter((row) => row.status === 'pending');
        const writable = open.filter((row) => row.fixability === 'writable');
        const failures = open.filter((row) => row.lastError);
        return (
          <Alert key={record.id} severity={open.length > 0 ? 'info' : 'success'} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Barcode override: {record.replacedBarcodes.join(' / ') || 'none'} to {record.newBarcode}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <StatusChip tone={open.length > 0 ? 'caution' : 'positive'} label={`${open.length} of ${record.corrections.length} listings open`} />
              {failures.length > 0 ? <StatusChip tone="problem" label={`${failures.length} failed`} /> : null}
            </Stack>
            {failures.map((row) => (
              <Typography key={row.id} variant="caption" component="div" color="text.secondary">
                {row.integrationName || row.platform}: {row.lastError}
              </Typography>
            ))}
            {open.length > 0 ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => setConfirm({ record, action: 'notify' })}>
                  Notify stores not yet told
                </Button>
                <Button size="small" variant="outlined" disabled={writable.length === 0} onClick={() => setConfirm({ record, action: 'push' })}>
                  Push the fix to {writable.length} listing{writable.length === 1 ? '' : 's'}
                </Button>
              </Stack>
            ) : null}
          </Alert>
        );
      })}
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.action === 'push' ? 'Push the corrected barcode' : 'Notify the stores'}
        message={confirm?.action === 'push'
          ? "This runs each store's normal sync for this product and writes the corrected barcode to its listing, where the listing still carries the old one. Each store is told."
          : 'Each store not yet told sees the change. Nothing is written to any store.'}
        confirmLabel={confirm?.action === 'push' ? 'Push the fix' : 'Notify'}
        loading={run.isPending}
        onConfirm={() => run.mutate()}
        onClose={() => setConfirm(null)}
      />
    </>
  );
};
